import { useEffect, useState } from "react";
import {
  ArrowUpRight,
  CheckCircle2,
  Eye,
  FileBarChart,
  Lightbulb,
  Loader2,
  Save,
  X,
} from "lucide-react";
import {
  authorNativeReport,
  getAuthoringConfig,
  previewNativeReport,
  promoteAuthored,
  suggestReports,
  type AuthoringApiError,
  type AuthoringConfig,
  type AuthorResult,
  type NativePlanInput,
  type PreviewResult,
  type ReportIdea,
} from "@/lib/authoringApi";

type Props = {
  open: boolean;
  /** The picked data module / package the report is built from. */
  model: { id: string | null; path: string; name: string; objectClass: string };
  onClose: () => void;
};

const DISABLED_CFG: AuthoringConfig = {
  enabled: false, allowExplorations: false,
  requireStaging: true, stagingFolder: "", stagingConfigured: false,
};

const LAYOUTS = [
  { value: "", label: "Auto" },
  { value: "list", label: "List" },
  { value: "crosstab", label: "Crosstab" },
  { value: "chart", label: "Chart" },
];

/**
 * Build a native live-query report from a data module / package, the
 * no-chat way: ask for ideas, preview the rows transiently (no persist),
 * then author into the staging folder and promote.
 *
 * Suggest + Preview are read-only and work whenever the SDK (and, for
 * suggest, the LLM gateway) is up. Author + Promote are gated by
 * {@code datumcraft.authoring.enabled}; when off we still let the user
 * preview and explain why the write buttons are disabled.
 */
export function AuthorReportModal({ open, model, onClose }: Props) {
  const modelId = model.id || model.path;

  const [cfg, setCfg] = useState<AuthoringConfig>(DISABLED_CFG);

  const [ideas, setIdeas] = useState<ReportIdea[]>([]);
  const [ideasLoading, setIdeasLoading] = useState(false);
  const [ideasError, setIdeasError] = useState<string | null>(null);

  const [reportName, setReportName] = useState("");
  const [intent, setIntent] = useState("");
  const [layoutKind, setLayoutKind] = useState("");
  const [vizType, setVizType] = useState("");
  const [categories, setCategories] = useState<string[]>([]);
  const [measures, setMeasures] = useState<string[]>([]);

  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [authoring, setAuthoring] = useState(false);
  const [authored, setAuthored] = useState<AuthorResult | null>(null);
  const [authorError, setAuthorError] = useState<string | null>(null);

  const [targetFolder, setTargetFolder] = useState("");
  const [promoting, setPromoting] = useState(false);
  const [promoted, setPromoted] = useState<AuthorResult | null>(null);
  const [promoteError, setPromoteError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setIdeas([]); setIdeasError(null);
    setReportName(""); setIntent(""); setLayoutKind(""); setVizType("");
    setCategories([]); setMeasures([]);
    setPreview(null); setPreviewError(null);
    setAuthored(null); setAuthorError(null);
    setTargetFolder(""); setPromoted(null); setPromoteError(null);
    getAuthoringConfig().then(setCfg).catch(() => setCfg(DISABLED_CFG));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  function currentPlan(): NativePlanInput {
    const hasRoles = categories.length > 0 || measures.length > 0;
    return {
      modelId,
      intent: hasRoles ? undefined : (intent.trim() || undefined),
      layoutKind: layoutKind || undefined,
      vizType: vizType || undefined,
      categories: categories.length ? categories : undefined,
      measures: measures.length ? measures : undefined,
    };
  }

  function applyIdea(idea: ReportIdea) {
    setReportName(idea.title);
    setIntent(idea.rationale ?? idea.prompt ?? "");
    setLayoutKind(idea.layoutKind ?? "");
    setVizType(idea.vizType ?? "");
    setCategories(idea.categories ?? []);
    setMeasures(idea.measures ?? []);
    setPreview(null); setPreviewError(null);
    setAuthored(null); setAuthorError(null);
  }

  async function loadIdeas() {
    setIdeasError(null); setIdeasLoading(true);
    try {
      setIdeas(await suggestReports(modelId, 6));
    } catch (e) {
      setIdeasError(errMsg(e));
    } finally {
      setIdeasLoading(false);
    }
  }

  async function doPreview() {
    setPreviewError(null); setPreview(null); setPreviewLoading(true);
    try {
      const r = await previewNativeReport(currentPlan());
      if (!r.success) setPreviewError("Preview run failed — the model may not support this layout.");
      else setPreview(r);
    } catch (e) {
      setPreviewError(errMsg(e));
    } finally {
      setPreviewLoading(false);
    }
  }

  async function doAuthor() {
    if (!reportName.trim()) { setAuthorError("Give the report a name first."); return; }
    setAuthorError(null); setAuthored(null); setAuthoring(true);
    try {
      const r = await authorNativeReport({ ...currentPlan(), reportName: reportName.trim() });
      if (!r.success) setAuthorError("Cognos rejected the write.");
      else setAuthored(r);
    } catch (e) {
      setAuthorError(errMsg(e));
    } finally {
      setAuthoring(false);
    }
  }

  async function doPromote() {
    if (!authored?.searchPath || !targetFolder.trim()) return;
    setPromoteError(null); setPromoting(true);
    try {
      const r = await promoteAuthored(authored.searchPath, targetFolder.trim());
      if (!r.success) setPromoteError("Promote failed.");
      else setPromoted(r);
    } catch (e) {
      setPromoteError(errMsg(e));
    } finally {
      setPromoting(false);
    }
  }

  const canAuthor = cfg.enabled && reportName.trim().length > 0 && !authoring;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-datumcraft-ink/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={`Build a report from ${model.name}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="flex max-h-[88vh] w-[min(940px,96vw)] flex-col overflow-hidden rounded-2xl border border-datumcraft-slate/25 bg-white shadow-elev">
        <header className="flex items-center justify-between border-b border-datumcraft-slate/20 px-5 py-3">
          <h2 className="flex items-center gap-2 font-display text-base font-semibold text-datumcraft-navy">
            <FileBarChart size={16} className="text-datumcraft-accentDark" aria-hidden />
            <span className="truncate">Build a report — {model.name}</span>
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-datumcraft-slate hover:bg-datumcraft-mist hover:text-datumcraft-ink"
          >
            <X size={18} />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {!cfg.enabled && (
            <p className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Authoring writes are disabled (<code>datumcraft.authoring.enabled=false</code>).
              You can still <strong>preview</strong> a report below — staging &amp; promote stay locked until it's on.
            </p>
          )}

          {/* 1) Suggested ideas */}
          <section className="mb-5">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="flex items-center gap-1.5 text-sm font-semibold text-datumcraft-navy">
                <Lightbulb size={14} className="text-datumcraft-accentDark" aria-hidden /> Suggested reports
              </h3>
              <button
                onClick={loadIdeas}
                disabled={ideasLoading}
                className="inline-flex items-center gap-1 rounded-md border border-datumcraft-accentDark/40 bg-white px-2.5 py-1 text-xs font-medium text-datumcraft-accentDark hover:bg-datumcraft-accent/10 disabled:opacity-50"
              >
                {ideasLoading ? <Loader2 size={12} className="animate-spin" /> : <Lightbulb size={12} />}
                {ideas.length > 0 ? "Refresh ideas" : "Suggest ideas"}
              </button>
            </div>
            {ideasError && (
              <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{ideasError}</p>
            )}
            {ideas.length > 0 && (
              <ul className="grid gap-2 sm:grid-cols-2">
                {ideas.map((idea, i) => (
                  <li key={i}>
                    <button
                      onClick={() => applyIdea(idea)}
                      className="flex h-full w-full flex-col gap-1 rounded-lg border border-datumcraft-slate/25 bg-datumcraft-mist/30 p-3 text-left hover:border-datumcraft-accentDark hover:bg-datumcraft-accent/5"
                    >
                      <span className="flex items-center gap-1.5 text-sm font-medium text-datumcraft-navy">
                        {idea.title}
                        {idea.layoutKind && (
                          <span className="rounded bg-datumcraft-navy/10 px-1.5 py-0.5 text-2xs uppercase tracking-wide text-datumcraft-navy">
                            {idea.layoutKind}{idea.vizType ? ` · ${idea.vizType}` : ""}
                          </span>
                        )}
                      </span>
                      {idea.rationale && (
                        <span className="text-xs text-datumcraft-slate">{idea.rationale}</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {!ideasLoading && ideas.length === 0 && !ideasError && (
              <p className="text-xs text-datumcraft-slate">
                Ask the designer for report ideas that fit this model's data — or describe your own below.
              </p>
            )}
          </section>

          {/* 2) Plan controls */}
          <section className="mb-5 grid gap-3">
            <label className="block">
              <span className="mb-1 block text-2xs font-semibold uppercase tracking-wider text-datumcraft-slate">
                What should the report show?
              </span>
              <textarea
                value={intent}
                onChange={(e) => setIntent(e.target.value)}
                rows={2}
                placeholder="e.g. Revenue by product line, highest first"
                className="w-full resize-y rounded-md border border-datumcraft-slate/30 bg-white px-2.5 py-1.5 text-sm text-datumcraft-ink focus:border-datumcraft-accentDark focus:outline-none"
              />
            </label>
            <div className="flex flex-wrap items-end gap-3">
              <label className="block">
                <span className="mb-1 block text-2xs font-semibold uppercase tracking-wider text-datumcraft-slate">Layout</span>
                <select
                  value={layoutKind}
                  onChange={(e) => setLayoutKind(e.target.value)}
                  className="rounded-md border border-datumcraft-slate/30 bg-white px-2 py-1.5 text-sm text-datumcraft-ink focus:border-datumcraft-accentDark focus:outline-none"
                >
                  {LAYOUTS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                </select>
              </label>
              {(categories.length > 0 || measures.length > 0) && (
                <div className="flex flex-1 flex-wrap items-center gap-1.5">
                  {categories.map((c) => (
                    <Chip key={`c-${c}`} label={c} tone="dim" onRemove={() => setCategories(categories.filter((x) => x !== c))} />
                  ))}
                  {measures.map((m) => (
                    <Chip key={`m-${m}`} label={m} tone="measure" onRemove={() => setMeasures(measures.filter((x) => x !== m))} />
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* 3) Preview */}
          <section className="mb-5">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="flex items-center gap-1.5 text-sm font-semibold text-datumcraft-navy">
                <Eye size={14} className="text-datumcraft-accentDark" aria-hidden /> Preview
                <span className="font-normal text-datumcraft-slate"> — runs the real query, nothing is saved</span>
              </h3>
              <button
                onClick={doPreview}
                disabled={previewLoading}
                className="inline-flex items-center gap-1 rounded-md bg-datumcraft-navy px-3 py-1 text-xs font-medium text-white hover:bg-datumcraft-ink disabled:opacity-50"
              >
                {previewLoading ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />} Preview rows
              </button>
            </div>
            {previewError && (
              <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{previewError}</p>
            )}
            {preview && (
              <div className="rounded-lg border border-datumcraft-slate/20">
                <div className="flex items-center gap-2 border-b border-datumcraft-slate/15 bg-datumcraft-mist/40 px-3 py-1.5 text-2xs text-datumcraft-slate">
                  <span className="rounded bg-datumcraft-navy/10 px-1.5 py-0.5 uppercase tracking-wide text-datumcraft-navy">{preview.layoutKind}</span>
                  <span>{preview.rowCount} row{preview.rowCount === 1 ? "" : "s"} · {preview.columns.length} column{preview.columns.length === 1 ? "" : "s"}</span>
                </div>
                <div className="max-h-64 overflow-auto">
                  <table className="w-full border-collapse text-xs">
                    <thead className="sticky top-0 bg-white">
                      <tr>
                        {preview.columns.map((c, i) => (
                          <th key={i} className="border-b border-datumcraft-slate/20 px-2.5 py-1.5 text-left font-semibold text-datumcraft-navy">{c}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((row, ri) => (
                        <tr key={ri} className={ri % 2 ? "bg-datumcraft-mist/20" : ""}>
                          {preview.columns.map((_, ci) => (
                            <td key={ci} className="border-b border-datumcraft-slate/10 px-2.5 py-1 text-datumcraft-ink">
                              {fmtCell(row[ci])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {preview.rows.length === 0 && (
                    <p className="px-3 py-3 text-xs text-datumcraft-slate">The query ran but returned no rows.</p>
                  )}
                </div>
              </div>
            )}
          </section>

          {/* 4) Author + promote */}
          <section className="rounded-lg border border-datumcraft-slate/20 bg-datumcraft-mist/20 p-4">
            <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-datumcraft-navy">
              <Save size={14} className="text-datumcraft-accentDark" aria-hidden /> Author to staging
            </h3>
            <div className="flex flex-wrap items-end gap-2">
              <label className="block flex-1">
                <span className="mb-1 block text-2xs font-semibold uppercase tracking-wider text-datumcraft-slate">Report name</span>
                <input
                  value={reportName}
                  onChange={(e) => setReportName(e.target.value)}
                  placeholder="Revenue by Product Line"
                  className="w-full rounded-md border border-datumcraft-slate/30 bg-white px-2.5 py-1.5 text-sm text-datumcraft-ink focus:border-datumcraft-accentDark focus:outline-none"
                />
              </label>
              <button
                onClick={doAuthor}
                disabled={!canAuthor}
                title={cfg.enabled ? "Create this report in the staging folder" : "Authoring writes are disabled"}
                className="inline-flex items-center gap-1 rounded-md bg-datumcraft-accentDark px-3 py-2 text-sm font-medium text-white hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {authoring ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Stage report
              </button>
            </div>
            {cfg.enabled && cfg.stagingFolder && (
              <p className="mt-1.5 text-2xs text-datumcraft-slate">Staging folder: <code>{cfg.stagingFolder}</code></p>
            )}
            {authorError && (
              <p className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{authorError}</p>
            )}

            {authored?.searchPath && (
              <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 p-3">
                <p className="flex items-center gap-1.5 text-sm font-medium text-emerald-800">
                  <CheckCircle2 size={15} /> Staged
                </p>
                <p className="mt-0.5 break-all text-2xs text-emerald-700"><code>{authored.searchPath}</code></p>

                <div className="mt-3 flex flex-wrap items-end gap-2">
                  <label className="block flex-1">
                    <span className="mb-1 block text-2xs font-semibold uppercase tracking-wider text-datumcraft-slate">Promote to folder</span>
                    <input
                      value={targetFolder}
                      onChange={(e) => setTargetFolder(e.target.value)}
                      placeholder="/content/folder[@name='Reports']"
                      className="w-full rounded-md border border-datumcraft-slate/30 bg-white px-2.5 py-1.5 text-xs text-datumcraft-ink focus:border-datumcraft-accentDark focus:outline-none"
                    />
                  </label>
                  <button
                    onClick={doPromote}
                    disabled={promoting || !targetFolder.trim() || !!promoted}
                    className="inline-flex items-center gap-1 rounded-md bg-datumcraft-navy px-3 py-2 text-sm font-medium text-white hover:bg-datumcraft-ink disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {promoting ? <Loader2 size={14} className="animate-spin" /> : <ArrowUpRight size={14} />} Promote
                  </button>
                </div>
                {promoteError && (
                  <p className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{promoteError}</p>
                )}
                {promoted?.searchPath && (
                  <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-emerald-800">
                    <CheckCircle2 size={14} /> Promoted to <code className="break-all">{promoted.searchPath}</code>
                  </p>
                )}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function Chip({ label, tone, onRemove }: { label: string; tone: "dim" | "measure"; onRemove: () => void }) {
  const cls = tone === "measure"
    ? "border-datumcraft-accentDark/40 bg-datumcraft-accent/10 text-datumcraft-accentDark"
    : "border-datumcraft-slate/30 bg-white text-datumcraft-ink";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-2xs ${cls}`}>
      {label}
      <button onClick={onRemove} aria-label={`Remove ${label}`} className="opacity-60 hover:opacity-100">
        <X size={10} />
      </button>
    </span>
  );
}

function fmtCell(v: unknown): string {
  if (v === null || v === undefined) return "—";
  return String(v);
}

function errMsg(e: unknown): string {
  const a = e as AuthoringApiError;
  if (a && typeof a.detail === "string") return a.detail;
  return e instanceof Error ? e.message : "Request failed.";
}
